/**
 * The account password — change it, or set a first one. Both web portals.
 *
 * CHANGE (the account has a password). Clerk guards the change twice: the
 * payload carries the current password, and the session must have been
 * re-verified recently (a 403 otherwise, before the payload is read). The
 * step-up uses the current password the person just typed — Clerk's
 * `useReverification` intercepts the 403, we answer it with a first-factor
 * `password` verification, and Clerk retries the change with the fresh
 * reverification attached. One form, no second prompt.
 *
 * SET (an account made with Apple, Google, Microsoft or LinkedIn has no
 * password). There is no current password to prove anything with, so the
 * step-up is Clerk's own — a code to the sign-in email — through `useStepUp`.
 * Afterwards the account signs in either way.
 *
 * Neither ever throws: the outcome is a union, like `useStepUp`'s, so a
 * dismissed step-up cannot be mistaken for a failure. The failure carries
 * Clerk's error for the caller to put into words (`useAuthErrorMessage`).
 */

import { useReverification, useSession, useUser } from '@clerk/clerk-react';
import { useCallback, useRef } from 'react';

import { isStepUpCancelled, useStepUp } from './useStepUp';

export type PasswordOutcome =
  | { outcome: 'done' }
  /** The person dismissed the step-up. Not an error. */
  | { outcome: 'cancelled' }
  | { outcome: 'failed'; error: unknown };

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  /** End every other session once the password changes. */
  signOutOfOtherSessions: boolean;
}

export interface SetPasswordRequest {
  newPassword: string;
  signOutOfOtherSessions: boolean;
}

export function useAccountPassword() {
  const { user } = useUser();
  const { session } = useSession();
  const stepUp = useStepUp();

  // The current password, from submit to the step-up that reuses it — a ref,
  // read in the same tick, cleared as soon as the change settles.
  const currentPassword = useRef('');
  // Why a step-up we answered was cancelled (a wrong current password), so
  // it is reported as the failure it is, not as a dismissal.
  const stepUpFailure = useRef<unknown>(null);

  const updateWithCurrentPassword = useReverification(
    async (request: ChangePasswordRequest) => {
      if (!user) throw new Error('No signed-in user');
      return user.updatePassword(request);
    },
    {
      onNeedsReverification: async ({ complete, cancel, level }) => {
        if (!session || !currentPassword.current) {
          stepUpFailure.current = new Error('Step-up needed with no session or current password');
          cancel();
          return;
        }
        try {
          await session.startVerification({ level: level ?? 'first_factor' });
          await session.attemptFirstFactorVerification({
            strategy: 'password',
            password: currentPassword.current,
          });
          complete();
        } catch (error) {
          stepUpFailure.current = error;
          cancel();
        }
      },
    }
  );

  const change = useCallback(
    async (request: ChangePasswordRequest): Promise<PasswordOutcome> => {
      stepUpFailure.current = null;
      currentPassword.current = request.currentPassword;
      try {
        await updateWithCurrentPassword(request);
        return { outcome: 'done' };
      } catch (error) {
        if (!isStepUpCancelled(error)) return { outcome: 'failed', error };
        const cause = stepUpFailure.current;
        return cause == null ? { outcome: 'cancelled' } : { outcome: 'failed', error: cause };
      } finally {
        currentPassword.current = '';
        stepUpFailure.current = null;
      }
    },
    [updateWithCurrentPassword]
  );

  const set = useCallback(
    async (request: SetPasswordRequest): Promise<PasswordOutcome> => {
      if (!user) return { outcome: 'failed', error: new Error('No signed-in user') };
      const result = await stepUp(() =>
        user.updatePassword({
          newPassword: request.newPassword,
          signOutOfOtherSessions: request.signOutOfOtherSessions,
        })
      );
      switch (result.outcome) {
        case 'completed':
          return { outcome: 'done' };
        case 'cancelled':
          return { outcome: 'cancelled' };
        case 'failed':
          return { outcome: 'failed', error: result.error };
        default: {
          const exhaustive: never = result;
          throw new Error(`Unhandled step-up outcome: ${JSON.stringify(exhaustive)}`);
        }
      }
    },
    [user, stepUp]
  );

  return {
    /** `false` for an account made with a social provider and no password yet. */
    hasPassword: user?.passwordEnabled ?? false,
    change,
    set,
  };
}
